// this file is mid-transition to TS, so we're disabling some rules temporarily.

/* eslint-disable @typescript-eslint/no-unsafe-call */
// a library to wrap and simplify api calls
import { CancelTokenSource } from 'axios'
import qs from 'query-string'
import apisauce, {
  ApiResponse,
  ApisauceInstance,
  CancelToken,
} from '~/apisauce'
import { domains } from '~/constants/domains'
import URLS from '~/constants/urls'
import { BlockNumberResponse } from '~/queries/blockNumberQueries'
import {
  extensionType,
  ExtensionTypeLabel,
  ExtensionTypeValue,
} from '~/queries/extensionQueries'
import {
  AgentCustomStatus,
  AgentCustomStatusRequest,
  AgentCustomStatusResponse,
} from '~/types/AgentCustomStatus'
import {
  GetFaxListRequest,
  GetSnippetsRequest,
  GetVoicemailsRequest,
  IdOrderingFilter,
  UnreadVoicemailStatusFilter,
} from '~/types/ApiRequestTypes'
import {
  CallTranscriptInstance,
  FindCallTranscriptResult,
} from '~/types/CallTranscript'
import {
  AuthenticationResultCallback,
  AuthenticationResultCallbackData,
  AuthenticationType,
  CreateThreadResponse,
  GetAuthDataCallback,
  LoginByEmailPayload,
  LoginByEmailResponse,
  PostMessageParams,
  RefreshTokenResponse,
  SendMessageResponse,
} from '~/types/MangoApiClientTypes'
import { UserAgentEventLog } from '~/types/UserAgentEventLog'
import { stringifyQueryParams } from '~/utils/apiQueryUtils'
import { getAppVersion, isElectron } from '~/utils/common'
import { setAuthInterceptors } from '~/utils/mangoApiClient/interceptors/authenticationInterceptor'
import { setRateLimitInterceptors } from '~/utils/mangoApiClient/interceptors/rateLimitInterceptor'

import {
  AgentStatus,
  AppSessionJwts,
  BlockedNumber,
  BlockNumberRequest,
  Call,
  CallLog,
  CallsQueryParams,
  ClientError,
  Contact,
  ContactSource,
  ContactSourcesQueryParams,
  ContactsQueryParams,
  Extension,
  ExtensionInstance,
  ExtensionPreview,
  Fax,
  FaxInstance,
  Message,
  MessageSnippet,
  MessageSnippetPostResponse,
  MessageThread,
  PaginatedApiResult,
  Pbx,
  PhoneNumber,
  User,
  UserRoles,
  UsersQueryParams,
  Voicemail,
  VoicemailInstance,
} from '@mangovoice/types'
import { ApiError } from '@mangovoice/types'
import { CallsReport, CallsReportRequest } from '@mangovoice/types'

export interface UserAgentEventLogQueryParams {
  pbx_id?: number
  event_types?: string[]
  details?: string[]
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
  ordering?: string
}

type MarkFaxAsReadResponse = {
  message: string
  read_at: string // ISO timestamp
}

enum ApiTokenType {
  Jwt = 'jwt',
  Session = 'session',
}

let getAuthData: GetAuthDataCallback | undefined

const withJwt = (api: ApisauceInstance) => {
  const { accessToken: jwt } = getAuthData?.() ?? {}
  api.setHeader('Authorization', `Bearer ${jwt ?? ''}`)
  return api
}

const create = () => {
  const authApi = apisauce.create({
    baseURL: domains.MANGO_API(),
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 10 * 1000, // 10 sec.
  })

  const legacyApi = apisauce.create({
    baseURL: domains.JWT_AUTH(),
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 10 * 1000, // 10 sec.
  })

  const mangoApi = apisauce.create({
    baseURL: domains.MANGO_API(),
    headers: {
      Accept: 'application/json; indent=4',
      'Content-Type': 'application/json',
    },
    timeout: 10 * 1000, // 10 sec.
  })

  let authenticationResultCallback: AuthenticationResultCallback | undefined

  const loginByEmail = async (payload: LoginByEmailPayload) => {
    return await authApi.post<LoginByEmailResponse>(URLS.LOGIN, payload)
  }

  const getRefreshToken = async (): Promise<
    ApiResponse<AppSessionJwts, ApiError>
  > => {
    const { refreshToken } = getAuthData?.() ?? {}
    const refreshResult = await authApi.post<RefreshTokenResponse>(
      URLS.REFRESH_TOKEN,
      {
        refresh: refreshToken,
      },
    )

    const appSessionJwt: AppSessionJwts = {
      access_token: refreshResult.data?.access ?? '',
      refresh_token: refreshResult.data?.refresh ?? '',
    }

    authenticationResultCallback?.({
      authenticationType: AuthenticationType.RefreshAppSessionJwts,
      ok: refreshResult.ok,
      result: refreshResult.ok
        ? appSessionJwt
        : (refreshResult.data as ApiError),
    })

    return refreshResult as ApiResponse<AppSessionJwts, ApiError>
  }

  const setDefaultVersionHeaders = (apiSauceInstances: ApisauceInstance[]) => {
    const doGetAppVersion = async (): Promise<string> => {
      return await getAppVersion()
    }

    doGetAppVersion().then((version: string) => {
      apiSauceInstances.forEach((apiSauceInst) => {
        apiSauceInst.setHeader('x-mango-app', isElectron ? 'desktop' : 'web')
        apiSauceInst.setHeader('x-mango-app-version', version)
      })
    })
  }

  //interceptors run in reverse order - don't change the order of these registrations
  setAuthInterceptors(
    [
      { apiSauceInstance: legacyApi, tokenType: ApiTokenType.Jwt },
      { apiSauceInstance: mangoApi, tokenType: ApiTokenType.Jwt },
    ],
    (data: AuthenticationResultCallbackData) =>
      authenticationResultCallback?.(data),
    () => getAuthData?.(),
    //FIXME: I'm not sure when this changed, but the types are mismatched
    getRefreshToken,
  )

  // @ts-expect-error some types are mismatched
  setRateLimitInterceptors([legacyApi, mangoApi, authApi])
  setDefaultVersionHeaders([authApi, legacyApi, mangoApi])

  const setCallbacks = (
    getAuthDataCallback: GetAuthDataCallback,
    onAuthenticationResult: AuthenticationResultCallback,
  ) => {
    getAuthData = getAuthDataCallback
    authenticationResultCallback = onAuthenticationResult
  }

  // #region  Call History
  // Replaced with Local Call History
  const getCallList = () => withJwt(legacyApi).get(URLS.CALL_LIST)

  const getCalls = (params: CallsQueryParams, signal?: AbortSignal) => {
    const queryString = stringifyQueryParams(params)
    return withJwt(mangoApi).get<PaginatedApiResult<Call>, ApiError>(
      `${URLS.CALLS}?${queryString}`,
      undefined,
      { signal },
    )
  }

  const getCall = (callId: number, signal?: AbortSignal) =>
    withJwt(mangoApi).get<Call, ApiError>(
      `${URLS.CALLS}${callId}/`,
      undefined,
      { signal },
    )

  const getRecentCalls = (pbxId: string | number) =>
    withJwt(mangoApi).get<PaginatedApiResult<CallLog>, ApiError>(
      URLS.RECENT_CALLS,
      {
        pbx_id: pbxId,
        limit: 100,
      },
    )
  const getCall_Legacy = (callId) =>
    withJwt(mangoApi).get(`${URLS.RECENT_CALLS}${callId}/`)

  const postClickToCall = ({
    extensionId,
    phoneNumber,
    autoAnswer,
  }: {
    extensionId: number
    phoneNumber: string
    autoAnswer: boolean
  }) => {
    const uri = URLS.CLICK_TO_CALL.replace('{id}', extensionId.toString())
    return withJwt(mangoApi).post(uri, {
      phone_number: phoneNumber,
      auto_answer: autoAnswer ? 1 : 0,
    })
  }
  // #endregion

  // #region  Call Transcripts
  const getTranscribedCallList = (pbxId: number) => {
    const params: CallsQueryParams = {
      pbx_ids: [pbxId],
      has_transcript: true,
      ordering: ['-started_at'],
      limit: 250,
    }
    const queryString = stringifyQueryParams(params)
    const route = `${URLS.CALLS}?${queryString}`

    return withJwt(mangoApi).get<PaginatedApiResult<Call>, ApiError>(route)
  }

  const findCallTranscriptByCallId = (pbxId: number, callId: number) => {
    const queryParams = {
      pbx_id: pbxId,
      call_id: callId,
    }
    const queryString = qs.stringify(queryParams)
    const route = `${URLS.CALL_TRANSCRIPTS}?${queryString}`

    return withJwt(mangoApi).get<
      PaginatedApiResult<FindCallTranscriptResult>,
      ApiError
    >(route)
  }

  const getCallTranscript = (transcriptId: number) => {
    return withJwt(mangoApi).get<CallTranscriptInstance>(
      `${URLS.CALL_TRANSCRIPTS}${transcriptId}/`,
    )
  }
  // #endregion

  // #region  Voicemail
  const getVoicemailExtensions = (pbxId: number) => {
    const queryString = qs.stringify({
      pbx_id: pbxId,
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Extension>, ApiError>(
      `${URLS.EXTENSIONS}?${queryString}`,
    )
  }

  const getAllExtensions = (pbxId: number) => {
    const queryParams = {
      pbx_id: pbxId,
      include_voicemail_status: true,
      limit: 1000,
    }

    const queryString = qs.stringify(queryParams)

    return withJwt(mangoApi).get<PaginatedApiResult<Extension>, ApiError>(
      `${URLS.EXTENSIONS_SELECT}?${queryString}`,
    )
  }

  const moveVoicemail = async (
    voicemailId: number,
    extensionId: number,
  ): Promise<{ message: string }> => {
    console.log(
      'Attempting to move voicemail:',
      voicemailId,
      'to extension:',
      extensionId,
    )

    const response: ApiResponse<{ message: string }, ApiError> = await withJwt(
      mangoApi,
    ).put(`${URLS.VOICEMAILS}${voicemailId}/move-to-extension/`, {
      extension_id: extensionId,
    })

    if (!response.ok) {
      const errorData = response.data
      console.error('API Error Data:', errorData)
      const error: ApiError = {
        type: 'ApiError',
        status: response.status,
        code: errorData?.code,
        message: errorData?.message || 'Failed to move voicemail',
        parameters: errorData?.parameters,
      }
      throw error
    }
    return response.data as { message: string }
  }

  const getVoicemails = ({
    pbxId,
    extensionId,
    search,
    offset,
    limit = 25,
    unread = UnreadVoicemailStatusFilter.ALL,
  }: GetVoicemailsRequest) => {
    const queryString = qs.stringify({
      ordering: '-id',
      pbx_id: pbxId,
      extension_id: extensionId,
      search,
      limit,
      unread,
      ...(offset && { offset }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Voicemail>, ApiError>(
      `${URLS.VOICEMAILS}?${queryString}`,
    )
  }
  const getVoicemail = (voicemailId) =>
    withJwt(mangoApi).get<VoicemailInstance, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/`,
    )
  const markVoicemailRead = (voicemailId) =>
    withJwt(mangoApi).put<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/mark-as-read/`,
    )
  const markVoicemailUnread = (voicemailId) =>
    withJwt(mangoApi).put<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/mark-as-unread/`,
    )
  const deleteVoicemail = (voicemailId) =>
    withJwt(mangoApi).delete<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/`,
    )
  // #endregion

  // #region  Faxing
  const sendFax = ({
    formData,
    onUploadProgress,
    onRequestStarted,
  }: {
    formData: FormData
    onUploadProgress?: (e: ProgressEvent) => void
    onRequestStarted?: (token: CancelTokenSource) => void
  }) => {
    const source = CancelToken.source()
    onRequestStarted?.(source)
    const sendingFaxApi = withJwt(mangoApi).post(URLS.SEND_FAX, formData, {
      headers: {
        'Content-Type': `multipart/form-data;`,
      },
      onUploadProgress: (progressEvent) =>
        onUploadProgress?.(progressEvent as unknown as ProgressEvent),
      cancelToken: source.token,
      timeout: 60 * 1000,
    })
    return sendingFaxApi
  }

  const resendFax = ({ faxId }: { faxId: number }) => {
    const uri = `${URLS.FAXES}${faxId}/resend/`
    return withJwt(mangoApi).put<unknown, ApiError>(uri)
  }

  const getFaxList = (
    {
      pbxId,
      offset,
      search,
      direction,
      extension_id,
      unread,
      limit = 50,
      ordering = IdOrderingFilter.DESCENDING,
    }: GetFaxListRequest,
    signal?: AbortSignal,
  ) => {
    const queryString = qs.stringify({
      ordering,
      pbx_id: pbxId,
      offset,
      search,
      extension_id,
      unread,
      limit,
      ...(direction && { direction }), // This syntax cleans up the network logs to avoid creating blank query params
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Fax>, ApiError>(
      `${URLS.FAXES}?${queryString}`,
      {
        signal,
      },
    )
  }
  const getFax = (faxId: number, signal?: AbortSignal) =>
    withJwt(mangoApi).get<FaxInstance, ApiError>(
      `${URLS.FAXES}${faxId}/`,
      undefined,
      { signal },
    )

  const deleteFax = (faxId) => {
    return withJwt(mangoApi).delete(`${URLS.FAXES}${faxId}/`)
  }

  // /app/v1/faxes/{id}/move-to-virtual-fax/
  interface MoveFaxToVirtualFaxResponse {
    extension_id: number
  }
  const moveFaxToVirtualFax = (
    faxId: number,
    body: { extension_id: number },
  ) => {
    return withJwt(mangoApi).put<MoveFaxToVirtualFaxResponse, ApiError>(
      `${URLS.FAXES}${faxId}/move-to-virtual-fax/`,
      body,
    )
  }

  // /app/v1/faxes/{id}/mark-as-unread/
  interface MarkFaxAsUnreadResponse {
    message: string
  }
  const markFaxAsUnread = (faxId: number) => {
    return withJwt(mangoApi).put<
      MarkFaxAsUnreadResponse,
      ApiError | ClientError
    >(`${URLS.FAXES}${faxId}/mark-as-unread/`)
  }

  // /app/v1/faxes/{id}/mark-as-read/
  const markFaxAsRead = (faxId: number) => {
    return withJwt(mangoApi).put<MarkFaxAsReadResponse, ApiError | ClientError>(
      `${URLS.FAXES}${faxId}/mark-as-read/`,
    )
  }
  // #endregion

  // #region  Snippets
  const getSnippets = ({
    pbxId,
    offset,
    personal = false,
  }: GetSnippetsRequest) => {
    const resolvedPbxId = personal ? '' : (pbxId ?? '')
    const scope = personal ? 'user' : ''
    const queryString = qs.stringify({
      ordering: '-id',
      pbx_id: resolvedPbxId,
      scope,
      ...(offset && { offset }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageSnippet>, ApiError>(
      `${URLS.GET_SNIPPETS}?${queryString}`,
    )
  }

  const searchSnippets = (search: string) => {
    const queryString = qs.stringify({
      ordering: '-id',
      limit: 500,
      ...(search && { search }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageSnippet>, ApiError>(
      `${URLS.GET_SNIPPETS}?${queryString}`,
    )
  }

  const postSnippet = (payload) => {
    const { id } = payload
    if (id)
      return withJwt(mangoApi).put<MessageSnippetPostResponse, ApiError>(
        `${URLS.GET_SNIPPETS}${id}/`,
        {
          ...payload,
        },
      )
    return withJwt(mangoApi).post<MessageSnippetPostResponse, ApiError>(
      URLS.GET_SNIPPETS,
      { ...payload },
    )
  }
  const deleteSnippet = (snippetId) =>
    withJwt(mangoApi).delete<undefined, ApiError>(
      `${URLS.GET_SNIPPETS}${snippetId}/`,
    )

  // #endregion

  // #region  Numbers
  const getPhoneNumber = ({ phoneNumberId }: { phoneNumberId: number }) => {
    return withJwt(mangoApi).get(`${URLS.PHONE_NUMBERS}${phoneNumberId}/`)
  }
  const getPhoneNumbers = ({ pbx_id, offset = 0, limit = 25, search = '' }) => {
    const queryString = qs.stringify({
      pbx_id,
      offset,
      limit,
      ...(search && { search }),
    })
    return withJwt(mangoApi).get<PaginatedApiResult<PhoneNumber>, ApiError>(
      `${URLS.PHONE_NUMBERS}?${queryString}`,
    )
  }
  // #endregion

  // #region PBX
  const getUserPbxList = (params?: { limit?: number }) => {
    const queryString = stringifyQueryParams({
      limit: params?.limit ?? 100,
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Pbx>, ApiError>(
      `${URLS.GET_PBXES}?${queryString}`,
    )
  }

  const getPbx = (pbxId: number | string) => {
    return withJwt(mangoApi).get<Pbx, ApiError>(`${URLS.GET_PBXES}${pbxId}/`)
  }

  const getPbxUsers = (params?: UsersQueryParams, signal?: AbortSignal) => {
    const queryString = stringifyQueryParams(params ?? {})
    return withJwt(mangoApi).get<PaginatedApiResult<User>, ApiError>(
      `${URLS.GET_PBX_USERS}?${queryString}`,
      undefined,
      { signal },
    )
  }

  const getUserRoles = (userId: number) => {
    return withJwt(mangoApi).get<UserRoles, ApiError>(`/users/${userId}/roles/`)
  }
  // #endregion

  // #region  SIP Connection
  const getSipConnectionInfo = (extensionId) => {
    return withJwt(mangoApi).get(
      `${URLS.EXTENSIONS}${extensionId}/sip-connection-info/`,
    )
  }
  // #endregion

  // #region Extensions

  const getPbxExtensions = (
    pbxId: string | number,
    type: ExtensionTypeValue = extensionType('Line'),
    count = 999,
  ): Promise<ApiResponse<PaginatedApiResult<ExtensionPreview>, ApiError>> => {
    const queryString = qs.stringify({
      pbx_id: pbxId,
      type: type,
      limit: count,
    })
    return withJwt(mangoApi).get(`${URLS.EXTENSIONS}select/?${queryString}`)
  }

  const getUserExtensions = (pbxId?: number) => {
    const options = qs.stringify({
      pbx_id: pbxId,
      type: extensionType('Line'),
      limit: 999,
      ordering: 'name',
    })

    return withJwt(mangoApi).get<
      PaginatedApiResult<ExtensionInstance>,
      ApiError
    >(`${URLS.EXTENSIONS}?${options}`)
  }

  interface GetExtensionsParams {
    search?: string
    pbx_id?: number
    offset?: number
    limit?: number
    type?: ExtensionTypeLabel
  }
  //get all extensions on login
  const getExtensions = ({
    search,
    pbx_id,
    type = 'Line',
    offset,
    limit,
  }: GetExtensionsParams) => {
    const queryString = qs.stringify({
      offset,
      limit,
      search,
      pbx_id,
      type: extensionType(type),
    })
    return withJwt(mangoApi).get<
      PaginatedApiResult<ExtensionInstance>,
      ApiError
      // We added an extended timeout to accommodate customers with exceptionally high extension counts (statistical tail)
    >(`${URLS.EXTENSIONS}?${queryString}`, undefined, { timeout: 60 * 1000 })
  }

  const getExtensionInfo = (
    extensionId?: number | string,
  ): Promise<ApiResponse<ExtensionInstance, ApiError>> =>
    withJwt(mangoApi).get(`/extensions/${extensionId ? `${extensionId}/` : ''}`)

  // #endregion

  // #region Messaging

  // Message Threads
  const getThreads = ({
    group_id,
    external_phone_number,
  }: {
    group_id?: string | number
    external_phone_number?: string
  }) => {
    return withJwt(mangoApi).get(URLS.MESSAGE_THREADS, {
      message_group_id: group_id,
      external_phone_number,
    })
  }

  const createThread = ({
    groupId,
    externalPhoneNumber,
    name,
  }: {
    groupId: number
    externalPhoneNumber: string
    name?: string
  }) =>
    withJwt(mangoApi).post<CreateThreadResponse, ApiError>(
      URLS.MESSAGE_THREADS,
      {
        group_id: groupId,
        external_phone_number: externalPhoneNumber,
        name,
      },
    )

  // Message Groups
  const getMessageGroups = (pbxId) => {
    const query = qs.stringify({
      pbx_id: pbxId,
    })
    return withJwt(mangoApi).get(`/message-groups/?${query}`)
  }

  const getMessageThread = (threadId: number) => {
    return withJwt(mangoApi).get<MessageThread, ApiError>(
      `/message-threads/${threadId}/`,
    )
  }

  // Message Threads
  const getMessageThreads = ({
    offset,
    search,
    groupId,
    pbxId,
    hasUnreadMessages,
    contactId,
    limit,
  }: {
    offset?: number | string
    search?: string
    groupId?: number | string
    pbxId?: number | string
    hasUnreadMessages?: boolean
    contactId?: number
    limit?: number
  }) => {
    const queryString = qs.stringify({
      ordering: '-last_message,-id',
      limit: limit ?? 25,
      offset: offset ?? 0,
      pbx_id: pbxId ?? undefined,
      group_id: groupId ?? undefined,
      message_group_id: groupId ?? undefined,
      has_unread_messages: hasUnreadMessages ?? undefined,
      search: search ?? undefined,
      contact_id: contactId ?? undefined,
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageThread>, ApiError>(
      `/message-threads/?${queryString}`,
    )
  }
  const updateThreadName = ({
    threadId,
    name,
  }: {
    threadId: number
    name: string
  }) => {
    return withJwt(mangoApi).put<MessageThread, ApiError>(
      `/message-threads/${threadId}/`,
      {
        name,
      },
    )
  }

  const deleteThread = (threadId) => {
    return withJwt(mangoApi).delete(`/message-threads/${threadId}/`)
  }
  const markMessageThreadAsRead = (threadId: number) => {
    return withJwt(mangoApi).put(`/message-threads/${threadId}/mark-as-read/`)
  }

  const markMessageGroupAsRead = (
    groupId: number,
  ): Promise<ApiResponse<{ message: string }, ApiError>> => {
    return withJwt(mangoApi).put(`/message-groups/${groupId}/mark-as-read/`)
  }

  // Messages
  const getMessages = ({
    threadId,
    offset,
    limit,
  }: {
    threadId: number
    offset?: number
    limit?: number
  }): Promise<ApiResponse<PaginatedApiResult<Message>, ApiError>> => {
    const queryParams = qs.stringify({
      thread_id: threadId,
      ordering: '-id',
      offset: offset ?? 0,
      limit: limit ?? 50,
    })
    return withJwt(mangoApi).get(`${URLS.GET_MESSAGES}?${queryParams}`)
  }

  const postMessage = ({
    threadId,
    body,
    file,
    onProgress,
  }: PostMessageParams): Promise<
    ApiResponse<SendMessageResponse, ApiError>
  > => {
    const formData = new FormData()
    formData.append('thread_id', threadId.toString())
    if (body) {
      formData.append('body', body)
    }
    if (file) {
      formData.append('file', file)
    }

    return withJwt(mangoApi).post<Message, ApiError>(
      URLS.GET_MESSAGES,
      formData,
      {
        onUploadProgress: (progressEvent) =>
          onProgress?.(progressEvent.progress ?? 0),
        headers: {
          'Content-Type':
            'multipart/form-data;boundary=----WebKitFormBoundaryyrV7KO0BoCBuDbTL',
        },
      },
    )
  }

  // #endregion

  // #region Contacts
  const getContactSources = (
    queryParams: ContactSourcesQueryParams = {
      limit: 25,
      offset: 0,
    },
  ) => {
    return withJwt(mangoApi).get<PaginatedApiResult<ContactSource>, ApiError>(
      `${URLS.CONTACT_SOURCES}?${qs.stringify(queryParams)}`,
    )
  }

  const getContactSource = (contactSourceId: number) => {
    return withJwt(mangoApi).get<ContactSource, ApiError>(
      `${URLS.CONTACT_SOURCES}${contactSourceId}/`,
    )
  }

  const getContacts = (
    queryParams: ContactsQueryParams,
    signal?: AbortSignal,
  ) => {
    return withJwt(mangoApi).get<PaginatedApiResult<Contact>, ApiError>(
      `${URLS.CONTACTS}?${qs.stringify(queryParams)}`,
      undefined,
      { signal },
    )
  }

  const getContact = (contactId: number) => {
    return withJwt(mangoApi).get<Contact, ApiError>(
      `${URLS.CONTACTS}${contactId}/`,
    )
  }
  // #endregion

  // #region Blocked Numbers
  const getBlockedNumbers = (pbxId: number) => {
    return withJwt(mangoApi).get<PaginatedApiResult<BlockedNumber>, ApiError>(
      `${URLS.BLOCKED_NUMBERS}?${qs.stringify({ pbx_id: pbxId, limit: 9999 })}`,
    )
  }

  const blockNumber = (pbxId: number, phoneNumber: string) => {
    const request: BlockNumberRequest = {
      pbx_id: pbxId,
      phone_number: phoneNumber,
    }
    return withJwt(mangoApi).post<BlockNumberResponse>(
      `${URLS.BLOCKED_NUMBERS}`,
      request,
    )
  }

  const unblockNumber = (blockedNumberId: number) => {
    return withJwt(mangoApi).delete(
      `${URLS.BLOCKED_NUMBERS}${blockedNumberId}/`,
    )
  }

  // #endregion

  // #region Call Queues
  const getQueueAgentStatus = (extensionId: number) => {
    return withJwt(mangoApi).get<AgentStatus, ApiError>(
      `${URLS.QUEUE_AGENT_STATUS}`.replace(
        '{extensionId}',
        extensionId.toString(),
      ),
    )
  }

  const putQueueLogin = (extensionId: number) => {
    return withJwt(mangoApi).put<undefined, ApiError>(
      `${URLS.QUEUE_LOG_IN}`.replace('{extensionId}', extensionId.toString()),
    )
  }

  const putQueueLogout = (extensionId: number) => {
    return withJwt(mangoApi).put<undefined, ApiError>(
      `${URLS.QUEUE_LOG_OUT}`.replace('{extensionId}', extensionId.toString()),
    )
  }

  // #endregion

  //#region Agent Status
  const getAgentStatuses = (pbxId?: number) => {
    return withJwt(mangoApi).get<AgentCustomStatusResponse[]>(
      `${URLS.CALL_CENTER_USER_AGENTS}`,
      {
        pbx_id: pbxId,
      },
    )
  }

  const getUserAgentStatus = (ext_uuid: string) => {
    return withJwt(mangoApi).get<AgentCustomStatusResponse, ApiError>(
      `${URLS.CALL_CENTER_USER_AGENTS}${ext_uuid}/`,
    )
  }

  const putQueueAgentStatus = (
    ext_uuid: string,
    customStatusId: number | null,
    inQueue?: boolean,
  ) => {
    return withJwt(mangoApi).put<undefined, ApiError>( //TODO correct return type
      `${URLS.CALL_CENTER_USER_AGENTS}${ext_uuid}/`,
      { agent_status_id: customStatusId, logged_in: inQueue },
    )
  }

  //#endregion

  //#region Custom Statuses
  const getCustomStatuses = (params?: object) => {
    return withJwt(mangoApi).get<PaginatedApiResult<AgentCustomStatus>>(
      URLS.CALL_CENTER_AGENT_STATUS,
      params,
    )
  }

  const postCustomStatus = (data: AgentCustomStatusRequest) => {
    return withJwt(mangoApi).post<AgentCustomStatus>(
      URLS.CALL_CENTER_AGENT_STATUS,
      data,
    )
  }

  const putCustomStatus = (
    statusId: number,
    data: AgentCustomStatusRequest,
  ) => {
    return withJwt(mangoApi).put<AgentCustomStatus>(
      `${URLS.CALL_CENTER_AGENT_STATUS}${statusId}/`,
      data,
    )
  }

  const deleteCustomStatus = (statusId: number) => {
    return withJwt(mangoApi).delete(
      `${URLS.CALL_CENTER_AGENT_STATUS}${statusId}/`,
    )
  }
  //#endregion

  //#region User Agent Event Logs
  const getUserAgentEventLogs = (params?: UserAgentEventLogQueryParams) => {
    return withJwt(mangoApi).get<
      PaginatedApiResult<UserAgentEventLog>,
      ApiError
    >(URLS.CALL_CENTER_USER_AGENT_EVENT_LOGS, {
      pbx_id: params?.pbx_id,
      event_type: params?.event_types?.join(','),
      details: params?.details?.join(','),
      start_date: params?.start_date,
      end_date: params?.end_date,
      limit: params?.limit ?? 200,
      offset: params?.offset ?? 0,
      ordering: params?.ordering ?? '-created_at',
    })
  }
  //#endregion

  //#region Reporting
  /**
   * Sends a calls report request to the Mango API and returns a calls report.
   *
   * @param reportRequest - The payload to build the report.
   * @param signal - Optional AbortSignal to cancel the request if necessary.
   * @returns A promise resolving to the ApiResponse containing the CallsReport or an ApiError.
   */
  const getCallsReport = (
    reportRequest: CallsReportRequest,
    signal?: AbortSignal,
  ) => {
    return withJwt(mangoApi).post<CallsReport, ApiError>(
      URLS.REPORTING_CALLS,
      reportRequest,
      { signal },
    )
  }
  //#endregion

  /**
   * Attempts to make a small api request to verify the user is able to access the api, is authenticated, and if not will attempt to refresh the token.
   * @returns true if successful, false if not
   */
  const verifyConnection = async (): Promise<boolean> => {
    // We use get pbxes as a simple endpoint to verify the user is authenticated. This could be replaced with any small and fast endpoint
    const result = await withJwt(mangoApi).get<
      PaginatedApiResult<Pbx>,
      ApiError
    >(`${URLS.GET_PBXES}?limit=1`)

    return result.ok
  }

  return {
    // Authentication
    setCallbacks,
    loginByEmail,
    verifyConnection,

    // Voicemails
    getVoicemailExtensions,
    getVoicemails,
    getVoicemail,
    markVoicemailRead,
    markVoicemailUnread,
    deleteVoicemail,

    // Calls / Transcripts
    getCallList,
    getRecentCalls,
    getTranscribedCallList,
    findCallTranscriptByCallId,
    getCalls,
    getCall,
    getCallTranscript,
    getCall_Legacy,
    postClickToCall,

    // Faxing
    getFaxList,
    getFax,
    sendFax,
    resendFax,
    moveFaxToVirtualFax,
    deleteFax,
    markFaxAsRead,
    markFaxAsUnread,

    // Extensions
    getExtensions,
    getPbxExtensions,
    getUserExtensions,
    getExtensionInfo,
    moveVoicemail,
    getAllExtensions,

    // Messaging / Threads
    getThreads,
    createThread,
    updateThreadName,
    getMessageGroups,
    getMessageThread,
    getMessageThreads,
    markMessageGroupAsRead,
    markMessageThreadAsRead,
    deleteThread,
    getMessages,
    postMessage,

    // Phone Numbers
    getPhoneNumber,
    getPhoneNumbers,

    // Snippets
    getSnippets,
    searchSnippets,
    postSnippet,
    deleteSnippet,

    // PBX
    getUserPbxList,
    getPbx,
    getPbxUsers,
    getUserRoles,

    // Contacts
    getContactSources,
    getContactSource,
    getContacts,
    getContact,

    // Blocked Numbers
    getBlockedNumbers,
    blockNumber,
    unblockNumber,

    // Call Queues / Agent Statuses
    getQueueAgentStatus,
    putQueueLogin,
    putQueueLogout,
    getUserAgentStatus,
    getAgentStatuses,
    putQueueAgentStatus,
    getCustomStatuses,
    postCustomStatus,
    putCustomStatus,
    deleteCustomStatus,
    getUserAgentEventLogs,

    // Dashboards / Reports
    getCallsReport,

    // Other
    getSipConnectionInfo,
  }
}

export function isClientError(candidate: any): candidate is ClientError {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return false
  }

  const requiredKeys = ['type']
  if (
    requiredKeys.some(
      (key) => !Object.prototype.hasOwnProperty.call(candidate, key),
    )
  ) {
    return false
  }

  return candidate.type === 'client_error'
}

function isIsoTimestamp(candidate: any): boolean {
  if (typeof candidate !== 'string') {
    return false
  }

  const isoRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|([+-]\d{2}:\d{2}))?$/
  return isoRegex.test(candidate) && !isNaN(Date.parse(candidate))
}

export function isMarkFaxAsReadResponse(
  candidate: any,
): candidate is MarkFaxAsReadResponse {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return false
  }

  const requiredKeys = ['message', 'read_at']
  if (
    requiredKeys.some(
      (key) => !Object.prototype.hasOwnProperty.call(candidate, key),
    )
  ) {
    return false
  }

  return (
    typeof candidate.message === 'string' && isIsoTimestamp(candidate.read_at)
  )
}

export default {
  create,
}
